import React, { useEffect, useState } from 'react';
import { notification } from 'antd';
import moment from 'moment';
import { GlassButton, GlassInput, GlassModal } from '../glass';
import {
  useDailyJournalMutation,
  useDeleteNoteMutation,
  useSaveNoteMutation,
} from '../slices/zerodhaApiSlice';
import TradeCalendar from './customeCalender';
import MyEditor from './Usercomp/MyEditor';
import DailyTradeDescription from './Usercomp/DailyTradeDescription';
import AreaGredientChartY from './Usercomp/AreaGredientChartY';

/**
 * The "Calendar" page (route /calender).
 * It loads the journal of the month on screen and opens the same day-note popup as the Dashboard.
 */
const CalendarScreen = () => {
  const [api, contextHolder] = notification.useNotification();

  const [dailyJournal, { isLoading: isJournalLoading }] = useDailyJournalMutation();
  const [saveNote, { isLoading: isSaveNoteLoading }] = useSaveNoteMutation();
  const [deleteNote] = useDeleteNoteMutation();

  // month shown by the calendar, e.g. "2026-10", and that month's daily journal rows
  const [monthKey, setMonthKey] = useState(moment().format('YYYY-MM'));
  const [journal, setJournal] = useState([]);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const start = moment(`${monthKey}-01`).startOf('month');

    (async () => {
      try {
        const res = await dailyJournal({
          startDate: start.toISOString(),
          endDate: start.clone().endOf('month').toISOString(),
          page: 1,
          pageSize: 1000,
        }).unwrap();
        if (!cancelled) setJournal(res?.data?.results || []);
      } catch (e) {
        if (!cancelled) setJournal([]);
      }
    })();

    return () => { cancelled = true; };
    // only reload when the month changes or after a note is saved/deleted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthKey, reload]);

  // ---- Day note popup (same behaviour as the Dashboard) ----
  const [modeldata, setModeldata] = useState({});
  const [content, setContent] = useState('');
  const [notetitle, setNotetitle] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (modeldata) {
      setNotetitle(modeldata?.note?.title || modeldata?.title || '');
      setContent(modeldata?.note?.content || modeldata?.content || '');
    }
  }, [modeldata]);

  const resetModal = () => {
    setIsModalOpen(false);
    setModeldata({});
    setNotetitle('');
    setContent('');
  };

  // The day's date. A journal row keeps it in `_id` (e.g. "2026-10-30"); an empty day has `trade_date`.
  const dayOf = (m) => m?.trade_date || m?.date || (/^\d{4}-\d{2}-\d{2}/.test(String(m?._id || '')) ? m._id : null);

  const handleOk = async () => {
    try {
      await saveNote({
        _id: modeldata?._id,
        trade_date: dayOf(modeldata),
        title: notetitle,
        content: content,
      }).unwrap();
      api.success({ message: 'Note Saved!' });
      resetModal();
      setReload((n) => n + 1);
    } catch (err) {
      api.error({ message: err?.data?.message || err.error });
    }
  };

  const handleDeleteNote = async () => {
    const tradeDate = dayOf(modeldata);
    if (!tradeDate) {
      api.error({ message: 'Could not find the date of this note' });
      return;
    }

    try {
      await deleteNote(moment(tradeDate).format('YYYY-MM-DD')).unwrap();
      api.success({ message: 'Note deleted' });
      resetModal();
      setReload((n) => n + 1);
    } catch (err) {
      api.error({ message: err?.data?.message || err?.data?.error || err?.error || 'Unable to delete note' });
    }
  };

  const dayPnl = (modeldata?.totalProfit || 0) - (modeldata?.totalLoss || 0);

  return (
    <>
      {contextHolder}

      <TradeCalendar
        title="Calendar"
        journalData={journal}
        onMonthChange={(d) => setMonthKey(moment(d).format('YYYY-MM'))}
        onOpenNote={(data) => {
          setModeldata(data);
          setIsModalOpen(true);
        }}
      />

      <GlassModal
        open={isModalOpen}
        onClose={resetModal}
        width={1000}
        title={`${moment(modeldata?._id || modeldata?.trade_date).format('ddd, D MMM, YYYY')}  ·  Realised P&L ₹ ${isJournalLoading ? 0 : dayPnl.toFixed(2)}`}
        footer={
          <>
            <GlassButton onClick={resetModal}>Cancel</GlassButton>
            <GlassButton variant="primary" disabled={isSaveNoteLoading} onClick={handleOk}>
              {isSaveNoteLoading ? 'Saving…' : 'Save note'}
            </GlassButton>
          </>
        }
      >
        <div className="glass-grid glass-grid--modal">
          <AreaGredientChartY datal={isJournalLoading ? [] : modeldata.runningPnlArray} />
          <DailyTradeDescription data={modeldata} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '12px 0' }}>
          <GlassButton variant="danger" onClick={handleDeleteNote}>Delete note</GlassButton>
        </div>

        <GlassInput
          placeholder="Title"
          value={notetitle}
          onChange={(e) => setNotetitle(e.target.value)}
        />
        <div style={{ marginTop: 10 }}>
          {isModalOpen && <MyEditor setContent={setContent} initialContent={content} />}
        </div>
      </GlassModal>
    </>
  );
};

export default CalendarScreen;